import { useNavigate } from 'react-router-dom';
import { LogOutIcon, UserIcon, SettingsIcon } from '@/config/icons';
import { MailIcon, CheckIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { useMyPendingInvitations } from '@/hooks/useMyPendingInvitations';

export function UserMenu() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { data: invitations = [], declineInvitation, isDeclining } = useMyPendingInvitations();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const getInitials = () => {
    const first = profile?.first_name?.charAt(0)?.toUpperCase() || '';
    const last = profile?.last_name?.charAt(0)?.toUpperCase() || '';
    return first + last || user?.email?.charAt(0)?.toUpperCase() || 'U';
  };

  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) return profile.first_name;
    return user?.email || 'Utilisateur';
  };

  const handleOpenInvitation = (workspaceId: string, token: string) => {
    navigate(`/auth?invite=${workspaceId}&token=${token}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative flex items-center gap-2 p-1.5 rounded-lg hover:bg-sidebar-accent transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Menu utilisateur"
        >
          <Avatar className="h-8 w-8">
            {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          {invitations.length > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center"
              aria-label={`${invitations.length} invitation(s) en attente`}
            >
              {invitations.length}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium truncate">{getDisplayName()}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          <UserIcon className="h-4 w-4 mr-2" />
          Mon profil
        </DropdownMenuItem>

        {invitations.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground font-normal">
              <MailIcon className="h-3.5 w-3.5" />
              Invitations en attente
              <Badge variant="secondary" className="ml-auto h-5">
                {invitations.length}
              </Badge>
            </DropdownMenuLabel>
            {invitations.map((inv) => (
              <div
                key={inv.id}
                className="flex items-start gap-1 px-2 py-1.5 rounded-sm hover:bg-accent"
              >
                <button
                  onClick={() => handleOpenInvitation(inv.workspace_id, inv.token)}
                  className="flex-1 min-w-0 text-left flex flex-col gap-0.5"
                  aria-label={`Accepter l'invitation à ${inv.workspace_name || 'cet espace'}`}
                >
                  <span className="text-sm font-medium truncate">
                    {inv.workspace_name || 'Espace de travail'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Rôle : {inv.role} · Expire le{' '}
                    {new Date(inv.expires_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </button>
                <button
                  onClick={() => handleOpenInvitation(inv.workspace_id, inv.token)}
                  className="p-1 rounded hover:bg-primary/10 text-primary"
                  aria-label="Accepter l'invitation"
                  title="Accepter"
                >
                  <CheckIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await declineInvitation({ workspace_id: inv.workspace_id, token: inv.token });
                      toast.success('Invitation refusée');
                    } catch {
                      toast.error('Impossible de refuser l\'invitation');
                    }
                  }}
                  disabled={isDeclining}
                  className="p-1 rounded hover:bg-destructive/10 text-destructive disabled:opacity-50"
                  aria-label="Refuser l'invitation"
                  title="Refuser"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onClick={() => navigate('/settings')}>
          <SettingsIcon className="h-4 w-4 mr-2" />
          Réglages
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
          <LogOutIcon className="h-4 w-4 mr-2" />
          Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
